import { BaseDataDefinition } from "./BaseDataDefinition";
import { DataTypes } from "../DataTypes";
import { ValidationIssue } from "../../IDesignValidationProvider";
import { JSONDummyData } from "../Constants/DataDefinitionConstants";
import type { AppGlueAny } from "../../AppGlueAny";

export class NumberDataDefinition extends BaseDataDefinition<number> {
    readonly type: DataTypes = DataTypes.NUMBER;

    lowerBounds?: number;
    upperBounds?: number;
    allowDecimals: boolean = true;

    static fromJSONObjectValue(value: AppGlueAny, name: string): NumberDataDefinition | undefined {
        if (Array.isArray(value)) {
            if (!value.find((v) => typeof v !== "number")) {
                let def = new NumberDataDefinition(name);
                def.list = true;

                return def;
            }
        } else if (typeof value === "number") {
            let def = new NumberDataDefinition(name);
            return def;
        }

        return undefined;
    }

    static fromJSONSchema(obj: object, name?: string): NumberDataDefinition | undefined {
        let def = new NumberDataDefinition(name);

        let type = Reflect.get(obj, "type");
        if (type === "array") {
            def.list = true;
            let items = Reflect.get(obj, "items");

            type = Reflect.get(items, "type");

            let upperBound = Reflect.get(items, "maximum");
            if (upperBound) {
                def.upperBounds = +upperBound;
            }

            let lowerBound = Reflect.get(items, "minimum");
            if (lowerBound) {
                def.lowerBounds = +lowerBound;
            }

            if (type === "number") {
                return def;
            } else if (type === "integer") {
                def.allowDecimals = false;
                return def;
            }
        } else if (type === "number") {
            let upperBound = Reflect.get(obj, "maximum");
            if (upperBound) {
                def.upperBounds = +upperBound;
            }

            let lowerBound = Reflect.get(obj, "minimum");
            if (lowerBound) {
                def.lowerBounds = +lowerBound;
            }

            return def;
        } else if (type === "integer") {
            let upperBound = Reflect.get(obj, "maximum");
            if (upperBound) {
                def.upperBounds = +upperBound;
            }

            let lowerBound = Reflect.get(obj, "minimum");
            if (lowerBound) {
                def.lowerBounds = +lowerBound;
            }
            def.allowDecimals = false;
            return def;
        }

        return undefined;
    }

    getStorageData(): object {
        let data = super.getStorageData();

        Reflect.set(data, "lowerBounds", this.lowerBounds);
        Reflect.set(data, "upperBounds", this.upperBounds);
        Reflect.set(data, "allowDecimals", this.allowDecimals);

        return data;
    }

    setStorageData(data: object) {
        super.setStorageData(data);

        this.lowerBounds = Reflect.get(data, "lowerBounds");
        this.upperBounds = Reflect.get(data, "upperBounds");
        this.allowDecimals = Reflect.get(data, "allowDecimals");
    }

    verifyValueType(value: AppGlueAny): ValidationIssue | undefined {
        if (value === undefined) return new ValidationIssue("Undefined Value");

        if (this.list) {
            // check is array
            if (!Array.isArray(value)) return new ValidationIssue(value + " is not a number array");

            // check each value is of right type
            for (let n of value) {
                if (typeof n !== "number" || isNaN(n))
                    return new ValidationIssue(value + " has array members that are not numbers");
            }
        } else {
            if (Array.isArray(value)) return new ValidationIssue(value + " is a number array");

            if (typeof value !== "number" || isNaN(value)) return new ValidationIssue(value + " is not a number");
        }

        return undefined;
    }

    convert(value: AppGlueAny): AppGlueAny {
        if (value === undefined) return undefined;

        if (this.list) {
            if (Array.isArray(value)) {
                let output: any[] = [];

                for (let n of value) {
                    output.push(+n);
                }

                return output;
            }
            return value;
        } else {
            if (Array.isArray(value)) {
                return +value[0];
            }
            return +value;
        }
    }

    toJSONObject(value: AppGlueAny): AppGlueAny {
        // need to convert to string
        let issue = this.verifyValueType(value);

        if (issue) {
            value = JSONDummyData.NUMBER;

            if (this.list) {
                value = [value];
            }
        }

        return value;
    }

    toJSONSchema(): object {
        if (this.list) {
            let obj = {
                type: "array",
                items: { type: this.allowDecimals ? "number" : "integer" },
            };

            if (this.upperBounds) {
                Reflect.set(obj.items, "maximum", this.upperBounds);
            }

            if (this.lowerBounds) {
                Reflect.set(obj.items, "minimum", this.lowerBounds);
            }

            return obj;
        }

        let obj = {
            type: this.allowDecimals ? "number" : "integer",
        };

        if (this.upperBounds) {
            Reflect.set(obj, "maximum", this.upperBounds);
        }

        if (this.lowerBounds) {
            Reflect.set(obj, "minimum", this.lowerBounds);
        }

        return obj;
    }
}
